import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { TrainingDay } from "../../../shared/src";
import {
  activateProgram,
  ApiError,
  createTrainingDay,
  deactivateProgram,
  deleteTrainingDay,
  getActiveWorkout,
  getProgram,
  listExercises,
  listTrainingDays,
  renameTrainingDay,
  reorderTrainingDays,
  startWorkout,
} from "../api-client";
import { DayExerciseSection } from "../components/DayExerciseSection";

export function ProgramDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const validId = Number.isInteger(id) && id > 0;
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const programKey = ["program", id] as const;
  const daysKey = ["trainingDays", id] as const;

  const programQuery = useQuery({
    queryKey: programKey,
    queryFn: () => getProgram(id),
    enabled: validId,
  });

  const daysQuery = useQuery({
    queryKey: daysKey,
    queryFn: () => listTrainingDays(id),
    enabled: validId,
  });

  const catalogQuery = useQuery({
    queryKey: ["exercises", { includeArchived: true }],
    queryFn: () => listExercises({ includeArchived: true }),
    enabled: validId,
  });

  function invalidateDays() {
    return queryClient.invalidateQueries({ queryKey: daysKey });
  }

  const [newName, setNewName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (name: string) => createTrainingDay(id, name),
    onSuccess: () => invalidateDays(),
  });

  const renameMutation = useMutation({
    mutationFn: ({ dayId, name }: { dayId: number; name: string }) =>
      renameTrainingDay(dayId, name),
    onSuccess: () => invalidateDays(),
  });

  const deleteMutation = useMutation({
    mutationFn: (dayId: number) => deleteTrainingDay(dayId),
    onSuccess: () => invalidateDays(),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) => reorderTrainingDays(id, ids),
    onSuccess: () => invalidateDays(),
  });

  const activeToggleMutation = useMutation({
    mutationFn: (next: boolean) =>
      next ? activateProgram(id) : deactivateProgram(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: programKey });
      void queryClient.invalidateQueries({ queryKey: ["programs"] });
    },
  });

  const startWorkoutMutation = useMutation({
    mutationFn: (dayId: number) => startWorkout({ training_day_id: dayId }),
    onSuccess: (workout) => {
      navigate(`/workouts/${workout.id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "CONFLICT") {
        void queryClient.invalidateQueries({ queryKey: ["activeWorkout"] });
      }
    },
  });

  const activeWorkoutForConflict = useQuery({
    queryKey: ["activeWorkout"] as const,
    queryFn: () => getActiveWorkout(),
    enabled:
      startWorkoutMutation.isError &&
      startWorkoutMutation.error instanceof ApiError &&
      startWorkoutMutation.error.code === "CONFLICT",
  });

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (trimmed.length === 0) return;
    setCreateError(null);
    try {
      await createMutation.mutateAsync(trimmed);
      setNewName("");
    } catch (err) {
      setCreateError(messageFor(err));
    }
  }

  function moveDay(day: TrainingDay, delta: -1 | 1) {
    const days = daysQuery.data ?? [];
    const index = days.findIndex((d) => d.id === day.id);
    const target = index + delta;
    if (target < 0 || target >= days.length) return;
    const reordered = [...days];
    const [picked] = reordered.splice(index, 1);
    if (!picked) return;
    reordered.splice(target, 0, picked);
    reorderMutation.mutate(reordered.map((d) => d.id));
  }

  function confirmDelete(day: TrainingDay) {
    if (window.confirm(`Delete training day "${day.name}"?`)) {
      deleteMutation.mutate(day.id);
    }
  }

  return (
    <main className="min-h-dvh p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">
          {programQuery.data ? programQuery.data.name : "Program"}
        </h1>
        <div className="flex items-center gap-3">
          {programQuery.data && (
            <button
              type="button"
              onClick={() =>
                activeToggleMutation.mutate(!programQuery.data!.is_active)
              }
              disabled={activeToggleMutation.isPending}
              aria-pressed={programQuery.data.is_active}
              aria-label={
                programQuery.data.is_active
                  ? "Mark as inactive"
                  : "Mark as active"
              }
              className={
                programQuery.data.is_active
                  ? "rounded-md border border-orange-500 bg-orange-500/10 px-3 py-1.5 text-sm text-orange-200 hover:bg-orange-500/20"
                  : "rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
              }
            >
              {programQuery.data.is_active ? "Active ✓" : "Mark active"}
            </button>
          )}
          <Link
            to="/programs"
            className="text-sm text-slate-400 hover:text-slate-200"
          >
            ← Programs
          </Link>
        </div>
      </header>

      {programQuery.isLoading && (
        <p className="text-sm text-slate-400">Loading…</p>
      )}
      {programQuery.isError && (
        <p className="text-sm text-red-400" role="alert">
          Failed to load this program.
        </p>
      )}
      {programQuery.data?.notes && (
        <p className="text-sm text-slate-300 whitespace-pre-line">
          {programQuery.data.notes}
        </p>
      )}

      <section className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3">
        <h2 className="text-sm font-medium text-slate-300">Training days</h2>

        <form onSubmit={onCreate} className="flex gap-2" aria-label="add day">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Push, Pull, Legs"
            maxLength={80}
            aria-label="day name"
            className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
          <button
            type="submit"
            disabled={
              createMutation.isPending || newName.trim().length === 0
            }
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
          >
            {createMutation.isPending ? "Adding…" : "Add"}
          </button>
        </form>
        {createError && (
          <p className="text-sm text-red-400" role="alert">
            {createError}
          </p>
        )}

        {daysQuery.data && daysQuery.data.length === 0 && (
          <p className="text-sm text-slate-400">
            No training days yet. Add one above to start structuring this
            program.
          </p>
        )}

        <ul className="space-y-3">
          {daysQuery.data?.map((day, index, all) => (
            <li
              key={day.id}
              className="rounded-md border border-slate-800 bg-slate-950"
            >
              <DayRow
                day={day}
                isFirst={index === 0}
                isLast={index === all.length - 1}
                onMoveUp={() => moveDay(day, -1)}
                onMoveDown={() => moveDay(day, 1)}
                onRename={(name) =>
                  renameMutation.mutateAsync({ dayId: day.id, name })
                }
                onDelete={() => confirmDelete(day)}
                onStartWorkout={() =>
                  startWorkoutMutation.mutate(day.id)
                }
                startBusy={
                  startWorkoutMutation.isPending &&
                  startWorkoutMutation.variables === day.id
                }
                startError={
                  startWorkoutMutation.isError &&
                  startWorkoutMutation.variables === day.id
                    ? messageFor(startWorkoutMutation.error)
                    : null
                }
                activeWorkoutId={
                  startWorkoutMutation.isError &&
                  startWorkoutMutation.variables === day.id &&
                  startWorkoutMutation.error instanceof ApiError &&
                  startWorkoutMutation.error.code === "CONFLICT"
                    ? activeWorkoutForConflict.data?.id ?? null
                    : null
                }
                busy={
                  reorderMutation.isPending ||
                  renameMutation.isPending ||
                  deleteMutation.isPending
                }
              />
              {catalogQuery.data && (
                <div className="px-3 pb-3">
                  <DayExerciseSection
                    dayId={day.id}
                    catalog={catalogQuery.data}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

interface DayRowProps {
  day: TrainingDay;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRename: (name: string) => Promise<unknown>;
  onDelete: () => void;
  onStartWorkout: () => void;
  startBusy: boolean;
  startError: string | null;
  activeWorkoutId: number | null;
  busy: boolean;
}

function DayRow({
  day,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onRename,
  onDelete,
  onStartWorkout,
  startBusy,
  startError,
  activeWorkoutId,
  busy,
}: DayRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(day.name);
  const [error, setError] = useState<string | null>(null);

  async function onSubmitRename(e: FormEvent) {
    e.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length === 0 || trimmed === day.name) {
      setEditing(false);
      return;
    }
    setError(null);
    try {
      await onRename(trimmed);
      setEditing(false);
    } catch (err) {
      setError(messageFor(err));
    }
  }

  return (
    <div>
      {editing ? (
        <form
          onSubmit={onSubmitRename}
          className="flex items-center gap-2 p-2"
          aria-label={`rename ${day.name}`}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            maxLength={80}
            aria-label="day name"
            className="flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-base"
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setDraft(day.name);
            }}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          {error && (
            <span className="ml-2 text-sm text-red-400" role="alert">
              {error}
            </span>
          )}
        </form>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 p-3">
          <span className="text-base">{day.name}</span>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={onStartWorkout}
              disabled={startBusy || busy}
              aria-label={`start workout for ${day.name}`}
              className="rounded-md bg-orange-600 px-3 py-1 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
            >
              {startBusy ? "Starting…" : "Start workout"}
            </button>
            <button
              type="button"
              onClick={onMoveUp}
              disabled={isFirst || busy}
              aria-label={`move ${day.name} up`}
              className="rounded-md border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800 disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={isLast || busy}
              aria-label={`move ${day.name} down`}
              className="rounded-md border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800 disabled:opacity-30"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-slate-700 px-3 py-1 text-sm hover:bg-slate-800"
            >
              Rename
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded-md border border-red-900 px-3 py-1 text-sm text-red-300 hover:bg-red-950"
            >
              Delete
            </button>
          </div>
          {startError && (
            <div className="w-full space-y-1" role="alert">
              <p className="text-sm text-red-400">{startError}</p>
              {activeWorkoutId !== null && (
                <Link
                  to={`/workouts/${activeWorkoutId}`}
                  className="inline-flex text-sm font-medium text-orange-400 hover:text-orange-300"
                >
                  Resume active workout →
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function messageFor(err: unknown): string {
  if (
    err instanceof ApiError &&
    (err.code === "BAD_REQUEST" || err.code === "CONFLICT")
  ) {
    return err.message;
  }
  return "Something went wrong. Try again.";
}
